import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './App.css';
import { trackEvent } from './analytics';

// Classify a download/CTA target into a stable platform enum for the
// download_click funnel event. Best-effort from the href + manifest key; never
// throws. See ANALYTICS_EVENT_SPEC.md (download_click).
export function classifyDownloadPlatform(href, ctaDownload, manifestKey) {
  const h = (href || '').toLowerCase();
  const mk = (manifestKey || '').toLowerCase();
  if (h.includes('apps.apple.com')) {
    // Mac App Store links carry mt=12 or the mac app id (6757826624);
    // the iOS app id is 6445939409.
    if (h.includes('mt=12') || h.includes('6757826624')) return 'macos_mas';
    return 'ios';
  }
  if (h.includes('play.google.com')) return 'android';
  if (h.endsWith('.dmg') || mk.includes('direct') || mk.includes('dmg')) return 'macos_dmg';
  if (mk.includes('shell') || h.includes('shell')) return 'shell';
  if (ctaDownload) return 'macos_dmg';
  return 'other';
}

const AppPage = ({ content, children }) => {
  const {
    title, subtitle, heroImage, heroImageAlt, heroImageStyle, heroContainerStyle,
    ctaText, ctaLink, ctaExternal, ctaDownload, ctaNote, manifestKey,
    sectionTitle, features, latestUpdates, latestUpdatesTitle
  } = content;

  const [buildInfo, setBuildInfo] = useState(null);

  useEffect(() => {
    if (!manifestKey) return;
    fetch('/downloads/manifest.json', { cache: 'no-cache' })
      .then(r => r.ok ? r.json() : null)
      .then(m => {
        const entry = m && m.products && m.products[manifestKey];
        if (entry) setBuildInfo(entry);
      })
      .catch(() => {});
  }, [manifestKey]);

  const renderCta = () => {
    const style = { display: 'inline-block', padding: '10px 24px', background: '#3b82f6', color: '#fff', borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem' };
    const href = buildInfo && buildInfo.downloadUrl ? buildInfo.downloadUrl : ctaLink;
    const platform = classifyDownloadPlatform(href, ctaDownload, manifestKey);
    const onClick = () => trackEvent('download_click', { platform, source_page: title || '' });
    if (ctaDownload) return <a href={href} download style={style} onClick={onClick}>{ctaText}</a>;
    if (ctaExternal) return <a href={href} target="_blank" rel="noopener noreferrer" style={style} onClick={onClick}>{ctaText}</a>;
    return <a href={href} style={style} onClick={onClick}>{ctaText}</a>;
  };

  const renderBuildLine = () => {
    if (!buildInfo) return null;
    const released = buildInfo.releasedAt ? new Date(buildInfo.releasedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : null;
    const sizeMb = buildInfo.sizeBytes ? (buildInfo.sizeBytes / (1024 * 1024)).toFixed(1) : null;
    const parts = [
      `v${buildInfo.version} (build ${buildInfo.build})`,
      released ? `released ${released}` : null,
      sizeMb ? `${sizeMb} MB` : null,
      buildInfo.minMacOS ? `macOS ${buildInfo.minMacOS}+` : null,
    ].filter(Boolean);
    return (
      <p style={{ color: '#9ca3af', fontSize: '0.78rem', marginTop: 8, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
        {parts.join(' · ')}
      </p>
    );
  };

  return (
    <div className="mp-page" style={{ background: '#fff' }}>
      {/* Hero */}
      <section style={{ textAlign: 'center', padding: '60px 24px 40px', maxWidth: 700, margin: '0 auto' }}>
        <h1 style={{ fontSize: '2.4rem', fontWeight: 800, color: '#111827', margin: '0 0 12px' }}>{title}</h1>
        <p style={{ fontSize: '1rem', color: '#6b7280', lineHeight: 1.6, margin: '0 0 20px' }}>{subtitle}</p>
        {renderCta()}
        {renderBuildLine()}
        {ctaNote && !buildInfo && <p style={{ color: '#6b7280', fontSize: '0.82rem', marginTop: 10 }}>{ctaNote}</p>}
        {heroImage && (
          <div style={{ marginTop: 32, ...heroContainerStyle }}>
            <img
              src={heroImage}
              alt={heroImageAlt || title}
              style={{ maxWidth: '100%', borderRadius: 12, border: '1px solid #e5e7eb', ...heroImageStyle }}
            />
          </div>
        )}
      </section>

      {/* Features */}
      {features && features.length > 0 && (
        <section style={{ maxWidth: 900, margin: '0 auto 48px', padding: '0 24px' }}>
          {sectionTitle && (
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', textAlign: 'center', marginBottom: 24 }}>
              {sectionTitle}
            </h2>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {features.map((feature, i) => (
              <div key={i} className="mp-card" style={{ padding: 20 }}>
                {feature.image && (
                  <img src={feature.image} alt={feature.imageAlt || feature.title}
                    style={{ width: '100%', borderRadius: 8, marginBottom: 12, ...feature.imageStyle }} />
                )}
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>
                  {feature.title}
                </h3>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', lineHeight: 1.5, margin: 0 }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Latest Updates */}
      {latestUpdates && latestUpdates.length > 0 && (
        <section style={{ maxWidth: 600, margin: '0 auto 48px', padding: '0 24px' }}>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827', textAlign: 'center', marginBottom: 16 }}>
            {latestUpdatesTitle || 'Latest Updates'}
          </h2>
          <div className="mp-card" style={{ padding: 20 }}>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {latestUpdates.map((update, i) => (
                <li key={i} style={{ color: '#374151', fontSize: '0.88rem', lineHeight: 1.8 }}>{update}</li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Children (Shell page architecture sections etc.) */}
      {children}

      {/* Footer */}
      <footer style={{ textAlign: 'center', padding: '32px 24px', borderTop: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginBottom: 8 }}>
          <Link to="/" style={{ color: '#6b7280', fontSize: '0.82rem', textDecoration: 'none' }}>Home</Link>
          <Link to="/docs" style={{ color: '#6b7280', fontSize: '0.82rem', textDecoration: 'none' }}>Docs</Link>
          <Link to="/privacy-policy" style={{ color: '#6b7280', fontSize: '0.82rem', textDecoration: 'none' }}>Privacy Policy</Link>
        </div>
        <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>
          &copy; {new Date().getFullYear()} mChatAI. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default AppPage;
